import { createRouter, createWebHistory } from "vue-router";
import { isTokenExpired } from "../composables/useAuth";
import { trackPageView } from "../utils/analytics";
import {
  clearChunkReloadState,
  reloadOnChunkLoadError,
} from "../utils/chunkReload";

const routes = [
  { path: "/", name: "Home", component: () => import("../views/HomePage.vue") },
  {
    path: "/category/:category_slug([^/]+)_cards",
    name: "Category",
    component: () => import("../views/CategoryPage.vue"),
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/LoginPage.vue"),
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/RegisterPage.vue"),
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/ResetPasswordPage.vue"),
  },
  {
    path: "/verify-email",
    name: "VerifyEmail",
    component: () => import("../views/VerifyEmailPage.vue"),
  },
  {
    path: "/about_us",
    name: "AboutUs",
    component: () => import("../views/AboutUsPage.vue"),
  },
  {
    path: "/contact_us",
    name: "ContactUs",
    component: () => import("../views/ContactUsPage.vue"),
  },
  {
    path: "/terms_of_service",
    name: "TermsOfService",
    component: () => import("../views/TermsOfServicePage.vue"),
  },
  {
    path: "/privacy_policy",
    name: "PrivacyPolicy",
    component: () => import("../views/PrivacyPolicyPage.vue"),
  },
  {
    path: "/render/inside-card",
    name: "InsideCardRender",
    component: () => import("../views/InsideCardRenderPage.vue"),
  },
  {
    path: "/card_present/:cart_item_id/:token",
    name: "CardPresent",
    component: () => import("../views/CardPresentPage.vue"),
    meta: { hideFooter: true },
  },
  // Temporarily disabled, not needed right now
  // { path: '/star_generator', name: 'StarGenerator', component: () => import('../views/StarGeneratorPage.vue') },
  {
    path: "/card/:category_slug/:card_slug",
    name: "Card",
    component: () => import("../views/CardPage.vue"),
  },
  {
    path: "/checkout",
    name: "Checkout",
    component: () => import("../views/CheckoutPage.vue"),
  },
  {
    path: "/orders",
    name: "Orders",
    component: () => import("../views/OrdersPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/orders/:id",
    name: "OrderDetail",
    component: () => import("../views/OrderDetailPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin",
    name: "Admin",
    component: () => import("../views/AdminPage.vue"),
    meta: { requiresStaff: true },
    redirect: () =>
      localStorage.getItem("isAdmin") === "true"
        ? { name: "AdminCategories" }
        : { name: "AdminCards" },
    children: [
      {
        path: "categories",
        name: "AdminCategories",
        component: () => import("../components/admin/CategoriesTab.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "cards",
        name: "AdminCards",
        component: () => import("../components/admin/CardsTab.vue"),
        meta: { requiresStaff: true },
      },
      {
        path: "users",
        name: "AdminUsers",
        component: () => import("../components/admin/UsersTab.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "orders",
        name: "AdminOrders",
        component: () => import("../components/admin/OrdersTab.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "server-health",
        name: "AdminServerHealth",
        component: () => import("../components/admin/ServerHealthTab.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "email-templates",
        name: "AdminEmailTemplates",
        component: () => import("../components/admin/EmailTemplatesTab.vue"),
        meta: { requiresAdmin: true },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFoundPage.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.afterEach((to) => {
  clearChunkReloadState();
  trackPageView(to.fullPath);
});

router.onError((error) => {
  reloadOnChunkLoadError(error);
});

// Navigation guard to protect auth/admin routes
router.beforeEach((to) => {
  const token = localStorage.getItem("token");
  const hasExpiredToken = token ? isTokenExpired(token) : false;

  if (hasExpiredToken) {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("email");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("isCreator");
  }

  if (to.meta.requiresAuth && (!token || hasExpiredToken)) {
    return { name: "Login", query: { redirect: to.fullPath } };
  }

  if (to.meta.requiresAdmin) {
    if (!token || hasExpiredToken) {
      return { name: "Login", query: { redirect: to.fullPath } };
    }
    // Let the AdminPage component verify admin status with the server
    // This handles cases where isAdmin might not be in localStorage yet
  }

  if (to.meta.requiresStaff) {
    if (!token || hasExpiredToken) {
      return { name: "Login", query: { redirect: to.fullPath } };
    }
  }
});

export default router;
